import { AxiosResponse } from "axios";
import { addSeconds } from "date-fns";
import { queryClient } from "..";
import { ROUTES } from "../routes";
import { CLIENT_SECRET } from "../env";
import { store } from "../store";
import { ROTATE_TOKENS_LOCK } from "../constants";
import { storeClear } from "../store/actions";
import { history } from "../Routing";
import { client } from "../services/axios";
import { firebaseUnregisterDevice } from "../services/firebase";
import {
  getTokenErrorLogLine,
  getTokenLogLine,
  logger,
} from "../services/logger";
import { UserCurrentT } from "../types/User";
import { HTTPMethod } from "../types/Logger";
import { ApiErrorT } from "../types/Common";
import {
  getTokens,
  removeCompany,
  removeTokens,
  setTokens,
} from "./localStorage";
import { getFullURL } from "./routing";
import { isDev } from "./permissions";

export const CLIENT_ID = "bootiq";
export const GRANT_TYPES = {
  password: "password",
  refreshToken: "refresh_token",
} as const;
export const TOKEN_ROTATION_BUFFER = 60; // 1 minute (s)

export type AuthMutation = {
  access_token: string;
  expires_in: number; // seconds
  refresh_token: string;
  token_type: string;
};

const rotateTokens = async (): Promise<AuthMutation> => {
  const tokens = await getTokens();
  try {
    const formData = new FormData();
    formData.append("grant_type", GRANT_TYPES.refreshToken);
    formData.append("client_id", CLIENT_ID);
    formData.append("client_secret", CLIENT_SECRET);
    formData.append("refresh_token", tokens?.refreshToken as string);

    const { data }: AxiosResponse<AuthMutation> = await client.post(
      "/api/oauth2/token",
      formData
    );

    const {
      expires_in: expiresIn,
      access_token: accessToken,
      refresh_token: newRefreshToken,
    } = data;
    const timeToExpiration = expiresIn - TOKEN_ROTATION_BUFFER; // seconds

    const accessTokenExpirationDate = addSeconds(new Date(), timeToExpiration);
    await setTokens(accessToken, newRefreshToken, accessTokenExpirationDate);

    logger.info(
      getTokenLogLine(
        {
          method: HTTPMethod.POST,
          url: getFullURL("/api/oauth2/token"),
        },
        GRANT_TYPES.refreshToken,
        isDev() ? tokens?.refreshToken : undefined
      )
    );

    return data;
  } catch (e) {
    logger.error(
      getTokenErrorLogLine(
        {
          method: HTTPMethod.POST,
          url: getFullURL("/api/oauth2/token"),
        },
        e as ApiErrorT,
        GRANT_TYPES.refreshToken,
        isDev() ? tokens?.refreshToken : undefined
      )
    );
    throw e;
  }
};

export const exclusiveRotateTokens = async () => {
  if (navigator.locks) {
    // Use a mutex lock in order to prevent more than one request to refresh the token
    // locks is widely supported so it should work on most browsers / devices
    const tokens: AuthMutation = await navigator.locks.request(
      ROTATE_TOKENS_LOCK,
      rotateTokens
    );
    return tokens;
  } else {
    return rotateTokens();
  }
};

export const getUserData = async (): Promise<UserCurrentT> => {
  const { data }: AxiosResponse<UserCurrentT> = await queryClient.fetchQuery(
    "current-user",
    () => client.get(`/api/v1/user/current`)
  );
  return data;
};

export const logoutUser = async () => {
  store.dispatch(storeClear());
  await removeTokens();
  await removeCompany();
  queryClient.invalidateQueries();
  await firebaseUnregisterDevice();
  history.push(ROUTES.login());
};
